import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import PropTypes, { any } from 'prop-types';

import Popup from '../../../Popup';
import { CheckBox, Input, Select } from '../../../Form';
import { eventQuestionTypes as eventQuestionTypeStrings } from '../../../../utils/eventConstants';
import Img from '../../../Img';
import styles from './addEventQuestionPopUp.scss';

const intToChar = (int) => {
    const code = 'A'.charCodeAt(0);
    return String.fromCharCode(code + int);
};

const AddEventQuestionPopUp = (props) => {
    const {
        formApi,
        isTemplate = false,
        onCloseClick,
        editQuestionData,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { eventQuestionTypes } = coreUI.types.tms;

    const defaultQuestionType = editQuestionData
        ? eventQuestionTypes.find(questionType => questionType.id === editQuestionData?.question?.type)?.value
        : eventQuestionTypeStrings.TEXT;

    const defaultAnswer = editQuestionData
        ? JSON.parse(editQuestionData?.question?.answer || '""')
        : null;

    const [type, setType] = useState(defaultQuestionType);
    const typeId = eventQuestionTypes.find(qt => qt.value === type)?.id;
    const [question, setQuestion] = useState(editQuestionData?.question?.text || '');
    const [answerOptions, setAnswerOptions] = useState(defaultAnswer);
    const [error, setError] = useState(null);

    const isMultipleChoiceType = type === eventQuestionTypeStrings.MULTIPLE_CHOICE;

    const isInEditMode = !!editQuestionData;

    const onSave = () => {
        const questionType = eventQuestionTypes.find(questionType => questionType.value === type);

        let error = null;
        if (question.length <= 0) {
            error = 'Question should not be empty';
        } else if (type === eventQuestionTypeStrings.MULTIPLE_CHOICE) {
            const validOptions = answerOptions?.reduce((acc, ans) => {
                return acc ? ans?.option?.length > 0 : false;
            }, true);

            if (!validOptions) {
                error = 'Options should not be empty';
            } else if (answerOptions?.length < 2) {
                error = 'Should have at least 2 options';
            } else {
                // Do nothing
            }
        } else {
            // Do nothing
        }

        if (error) {
            setError(error);
            return;
        }

        const answersString = (answerOptions && type === eventQuestionTypeStrings.MULTIPLE_CHOICE)
            ? JSON.stringify(answerOptions)
            : null;

        if (isInEditMode) {
            formApi.setValue(['eventQuestions', editQuestionData.index], {
                ...editQuestionData.question,
                text: question,
                type: questionType.id,
                answer: answersString,
            });
        } else {
            formApi.addValue('eventQuestions', {
                text: question,
                required: false,
                type: questionType.id,
                answer: answersString,
            });
        }
        onCloseClick();
    };

    const onQuestionInputChange = (ev) => {
        setQuestion(ev.target.value);
        if (error) {
            setError(null);
        }
    };

    const onChangeCheckbox = (checked) => {
        if (checked) {
            if (!answerOptions) {
                setAnswerOptions([
                    {
                        option: '',
                    },
                    {
                        option: '',
                    },
                ]);
            }
            setType(eventQuestionTypeStrings.MULTIPLE_CHOICE);
        } else {
            setType(eventQuestionTypeStrings.TEXT);
        }
    };

    const onChangeType = (typeId) => {
        const questionType = eventQuestionTypes.find(qt => qt.id === parseInt(typeId));
        setType(questionType?.value);
    };

    const eventQuestionTypesToShow = eventQuestionTypes.filter(questionType => questionType.value !== eventQuestionTypeStrings.PASSWORD);

    const onAnswerOptionChange = (ev, index) => {
        const newAnswers = cloneDeep(answerOptions);
        newAnswers[index].option = ev.target.value;
        setAnswerOptions(newAnswers);
        if (error) {
            setError(null);
        }
    };

    const removeAnswerOption = (index) => {
        const newAnswers = cloneDeep(answerOptions);
        newAnswers.splice(index, 1);
        setAnswerOptions(newAnswers);
        if (error) {
            setError(null);
        }
    };

    const onClickAddOption = (ev) => {
        ev.preventDefault();
        const newAnswers = cloneDeep(answerOptions) || [];
        newAnswers.push({
            option: '',
        });
        setAnswerOptions(newAnswers);
    };

    return (
        <Popup
            className="popup-md"
            config={{
                scrollable: false,
                showCloseIcon: false,
                style: {
                    maxHeight: '80vh',
                },
            }}
            onCloseClick={onCloseClick}
        >
            <div className={`${styles.addEventQuestionPopUp}`} data-testid="event-edit.question-popup">
                <div className="popup-section mb16">
                    <h5 className={styles.title} data-testid="event-edit.question-popup.title">
                        Add a question
                    </h5>
                </div>
                <div className="popup-section">
                    <div className={`${styles.questionHeading} mb8`}>
                        What is your question?
                    </div>
                    <Input
                        controlled={true}
                        id={`questionText`}
                        placeholder="Add a question"
                        formGroupCssClass="mb0"
                        value={question}
                        data-test={`questionTextInput`}
                        data-testid="event-edit.question-popup.question-input"
                        onChange={onQuestionInputChange}
                    />
                    {!isTemplate && (
                        <CheckBox
                            controlled={true}
                            id="questionText"
                            customLabel="This is a multiple choice question"
                            checked={isMultipleChoiceType}
                            formGroupCssClass={`mb0 mt16 ${styles.checkbox}`}
                            onChange={onChangeCheckbox}
                            data-testid="event-edit.question-popup.multiple-choice-checkbox"
                        />
                    )}
                    {isTemplate && (
                        <div className={`d-flex align-items-start mt16`} data-test={`questionTypeSelect`}>
                            <div className={`flex-1`}>
                                <div className={`${styles.questionHeading} form-group form-group-xl mt16`}>
                                    Question Type
                                </div>
                            </div>
                            <div className={`flex-1`}>
                                <Select
                                    controlled={true}
                                    hiddenLabel={true}
                                    hiddenDefault={true}
                                    id={`questionType`}
                                    value={typeId}
                                    options={eventQuestionTypesToShow}
                                    onChange={onChangeType}
                                    data-testid="event-edit.question-popup.question-type-select"
                                />
                            </div>
                        </div>
                    )}
                </div>
                {type === eventQuestionTypeStrings.MULTIPLE_CHOICE && (
                    <div className={`popup-section ${styles.answerOptionsSection}`}>
                        {answerOptions?.map((ans, index) => (
                            <div
                                key={`option-${intToChar(index)}`}
                                className={`${styles.answerOption} mt16 mb0`}
                            >
                                <div className={`${styles.questionHeading} mb8`}>
                                    {`Answer ${intToChar(index)}`}
                                </div>
                                <div className="d-flex align-items-center">
                                    <Input
                                        controlled={true}
                                        id={`questionText`}
                                        placeholder="Enter answer"
                                        formGroupCssClass="mb0 mr8 flex-1"
                                        value={ans.option}
                                        onChange={(ev) => onAnswerOptionChange(ev, index)}
                                        data-testid={`event-edit.question-popup.answer-option-${index}-input`}
                                    />
                                    <button
                                        className="ml8 btn btn-post"
                                        type="button"
                                        onClick={() => removeAnswerOption(index)}
                                        data-testid={`event-edit.question-popup.answer-option-${index}-delete-btn`}
                                    >
                                        <Img src={'/img/v3/icons/icon-trash-red.svg'} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <a
                            href="*"
                            className="text-small mt16 d-inline-block"
                            onClick={onClickAddOption}
                            data-testid="event-edit.question-popup.add-answer-link"
                        >
                            <Img
                                className="icon-16 rounded-icon-blue"
                                src={'/img/v2/icons/icon-plus-blue-small.svg'}
                            /> Add an answer
                        </a>
                    </div>
                )}
                {error && (
                    <div className={`popup-section mt8 text-small text-error ${styles.error}`} data-testid="event-edit.question-popup.error-msg">
                        {error}
                    </div>
                )}
                <div className="row">
                    <div className={`col-md-6 mt16`}>
                        <button
                            type="button"
                            className="btn btn-default btn-default text-uppercase btn-block"
                            onClick={onCloseClick}
                            data-testid="event-edit.question-popup.cancel-btn"
                        >
                            Cancel
                        </button>
                    </div>
                    <div className={`col-md-6 mt16`}>
                        <button
                            type="button"
                            className="btn btn-primary btn-default text-uppercase btn-block"
                            onClick={onSave}
                            data-testid="event-edit.question-popup.save-btn"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

AddEventQuestionPopUp.propTypes = {
    formApi: PropTypes.objectOf(any).isRequired,
    isTemplate: PropTypes.bool.isRequired,
    onCloseClick: PropTypes.func.isRequired,
    editQuestionData: PropTypes.objectOf({
        index: PropTypes.number,
        question: PropTypes.objectOf({
            type: PropTypes.number,
            answer: PropTypes.string,
            text: PropTypes.string,
        }),
    }),
};

AddEventQuestionPopUp.defaultProps = {
    editQuestionData: null,
};

export default AddEventQuestionPopUp;
